import { CommentRepository } from "@amityco/ts-sdk";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";

import { NOTIFICATION_CONTENT_TYPE, NOTIFICATION_TYPE } from "@/api";
import { Button, TextArea } from "@/components";
import { Image02 } from "@/icons";
import { useTranslate } from "@/providers/context";
import { cn } from "@/utils";

import { AttachmentPreview, CommunityUserAvatar } from "../..";
import {
  ACCEPTED_IMAGE_FILE_TYPES,
  MAX_ATTACHMENTS,
  MAX_FILE_SIZE,
  MAX_FILE_SIZE_LABEL,
} from "../../../const";
import { useCommunityError } from "../../../hooks/useCommunityError";
import { useSendCommunityNotification } from "../../../hooks/useSendCommunityNotification";
import { uploadMedia } from "../../../util";

const useAutoResize = (value: string | undefined) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }

    textarea.style.height = "44px";
    textarea.style.height = `${Math.max(44, textarea.scrollHeight)}px`;
  }, [value]);

  return textareaRef;
};

export const CommunityCommentInput = ({
  referenceId,
  parentId,
  replyingTo,
}: {
  referenceId: string;
  parentId?: string;
  replyingTo?: string;
}) => {
  const { translate } = useTranslate();
  const {
    handlePostingError,
    textContainsBlockedContent,
    showBlockedContentToast,
  } = useCommunityError();
  const sendNotification = useSendCommunityNotification();

  const [value, setValue] = useState<string | undefined>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [pendingAttachment, setPendingAttachment] = useState<File | null>(null);
  const [attachments, setAttachments] = useState<
    Amity.File<"image" | "video">[]
  >([]);
  const [isFocused, setIsFocused] = useState(false);

  const textareaRef = useAutoResize(value);
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle click outside to unfocus
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setTimeout(() => {
          setIsFocused(false);
        }, 100); // Small timeout to allow outside click to be handled before unfocusing and causing vertical position shift
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const isLoading = useMemo(
    () => isSubmitting || !!pendingAttachment || !!uploadProgress,
    [isSubmitting, pendingAttachment, uploadProgress],
  );

  const saveIsDisabled = useMemo(() => {
    if (isLoading || !value) {
      return true;
    }

    return false;
  }, [isLoading, value]);

  const handleAddAttachment = useCallback(
    async (file: File) => {
      if (!ACCEPTED_IMAGE_FILE_TYPES.includes(file.type)) {
        toast(translate("Invalid file type. Please select an image file."));
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        toast(
          `${translate("File size must be less than")} ${MAX_FILE_SIZE_LABEL}`,
        );
        return;
      }

      setUploadProgress(0);
      setPendingAttachment(file);

      try {
        const uploadedFile = await uploadMedia(file, setUploadProgress);
        setAttachments([...attachments, uploadedFile]);
      } catch (error) {
        toast(translate("File upload failed. Please try again."));
      } finally {
        setPendingAttachment(null);
        setUploadProgress(null);
      }
    },
    [
      attachments,
      setAttachments,
      setUploadProgress,
      setPendingAttachment,
      translate,
    ],
  );

  const handleSubmitComment = useCallback(async () => {
    setIsSubmitting(true);

    if (!value) {
      setIsSubmitting(false);
      return;
    }

    if (textContainsBlockedContent(value)) {
      showBlockedContentToast();
      setIsSubmitting(false);
      return;
    }

    try {
      const { data: newComment } = await CommentRepository.createComment({
        data: {
          text: value,
        },
        parentId,
        referenceId,
        referenceType: "post",
        attachments:
          attachments.length > 0
            ? attachments.map((a) => ({
                type: "image",
                fileId: a.fileId,
              }))
            : undefined,
      }).catch(handlePostingError);

      if (newComment) {
        sendNotification.mutate({
          notification_type: NOTIFICATION_TYPE.commentsAndReplies,
          content_type: replyingTo
            ? NOTIFICATION_CONTENT_TYPE.reply
            : NOTIFICATION_CONTENT_TYPE.comment,
          content_id: newComment.commentId,
          community_id: newComment.targetId,
          original_content_id: replyingTo ? parentId : referenceId,
          original_content_type: replyingTo
            ? NOTIFICATION_CONTENT_TYPE.comment
            : NOTIFICATION_CONTENT_TYPE.post,
        });
      }

      setValue("");
      setAttachments([]);
      setIsFocused(false);
    } catch (e) {
      console.error(e);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    parentId,
    referenceId,
    replyingTo,
    value,
    attachments,
    sendNotification,
    setValue,
    setIsSubmitting,
    handlePostingError,
    textContainsBlockedContent,
    showBlockedContentToast,
  ]);

  const onCancel = useCallback(() => {
    setValue("");
    setAttachments([]);
    setIsFocused(false);
  }, [setValue, setAttachments, setIsFocused]);

  return (
    <div className="flex w-full flex-col pr-[1px]" ref={containerRef}>
      <div className="flex gap-3 pt-1">
        <CommunityUserAvatar size="s" />
        <div
          className={cn(
            "flex w-full flex-col rounded-lg bg-surface-secondary transition-all duration-100 hover:bg-surface-tertiary",
            (isFocused || !!value || attachments.length > 0) &&
              "pb-[10px] outline outline-1 outline-border-active",
          )}
        >
          <TextArea
            ref={textareaRef}
            className="!p1-regular max-h-[320px] min-h-[44px] w-full resize-none !bg-transparent !outline-none"
            onChange={(e) => {
              setValue(e.target.value);
            }}
            onFocus={() => setIsFocused(true)}
            value={value}
            placeholder={
              replyingTo
                ? `${translate("Reply to")} ${replyingTo}`
                : `${translate("Leave a comment")}...`
            }
            disabled={isSubmitting}
          />
          <div className={"px-3"}>
            {(pendingAttachment || attachments.length > 0) && (
              <div className="mb-4 mt-1 flex h-[100px] w-fit max-w-full gap-2 overflow-x-auto">
                {attachments.map((file) => (
                  <AttachmentPreview
                    key={file.fileId}
                    file={file}
                    onRemove={() =>
                      setAttachments(
                        attachments.filter(
                          ({ fileId }) => fileId !== file.fileId,
                        ),
                      )
                    }
                  />
                ))}
                {pendingAttachment && (
                  <AttachmentPreview
                    pending={{
                      progress: uploadProgress ?? 0,
                      file: pendingAttachment,
                    }}
                  />
                )}
              </div>
            )}
            {(isFocused || !!value || attachments.length > 0) && (
              <div className="mt-1 flex w-full items-center justify-between">
                <Button
                  size="s"
                  variant="tertiary"
                  icon={<Image02 />}
                  className="h-fit rounded-md p-1"
                  disabled={isLoading || attachments.length >= MAX_ATTACHMENTS}
                  onClick={() => {
                    const fileInput = document.createElement("input");
                    fileInput.type = "file";
                    fileInput.accept = ACCEPTED_IMAGE_FILE_TYPES.join(", ");
                    fileInput.onchange = (event) => {
                      const file = (event.target as HTMLInputElement)
                        .files?.[0];
                      if (file) {
                        handleAddAttachment(file);
                      }
                    };
                    fileInput.click();
                  }}
                />
                <div className="flex gap-3">
                  <Button
                    variant="secondary"
                    className="bg-surface-secondary"
                    size="s"
                    onClick={onCancel}
                    disabled={isLoading}
                  >
                    {translate("Cancel")}
                  </Button>
                  <Button
                    size="s"
                    disabled={saveIsDisabled}
                    loading={isSubmitting}
                    onClick={handleSubmitComment}
                  >
                    {translate("Comment")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
